// PersonaCarousel.js — Financial Services AI Era Edition
import React, { useState } from "react";

/* =========================
   1. Design Tokens & CSS
   ========================= */
const CSS = `
  :root {
    --brand: #0056D2;       
    --brand-10: #E6F0FF;
    --navy: #0B1A3C;        
    --navy-dark: #081226;
    --white: #FFFFFF;
    --ink: #0B1A3C;
    --muted: #64748B;
    --line: #E2E8F0;
    
    --orange: #F97316;
    --green: #10B981;
    --green-bg: #ECFDF5;
    
    --radius-lg: 20px;
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .carousel-wrap {
    font-family: "Source Sans Pro", sans-serif;
    padding: 40px 20px;
    background: #F8FAFC;
  }

  .carousel-container {
    max-width: 1000px;
    margin: 0 auto;
  }

  /* --- The Dark Card (Lens Style) --- */
  .lens-card {
    background: var(--navy-dark); color: white;
    border-radius: var(--radius-lg);
    border: 1px solid rgba(255,255,255,0.1);
    box-shadow: var(--shadow-xl);
    overflow: hidden;
    position: relative;
    transition: all 0.3s ease;
  }

  .lens-header {
    padding: 32px 40px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    background: linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%);
  }

  .lens-tag {
    display: inline-block; background: var(--brand); color: white;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;
    padding: 6px 12px; border-radius: 99px; margin-bottom: 16px;
  }

  .lens-title { font-size: 32px; font-weight: 700; margin: 0 0 8px 0; letter-spacing: -0.5px; }
  .lens-sub { font-size: 16px; color: rgba(255,255,255,0.6); margin: 0; line-height: 1.5; max-width: 600px; }

  .lens-grid {
    display: grid; grid-template-columns: 1.1fr 0.9fr;
    min-height: 450px; /* consistent height */
  }
  @media (max-width: 800px) { .lens-grid { grid-template-columns: 1fr; } }

  .lens-left { padding: 40px; border-right: 1px solid rgba(255,255,255,0.1); }
  .lens-right { padding: 40px; background: rgba(255,255,255,0.02); }

  .lens-h3 {
    font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 1.2px;
    margin-bottom: 24px; display: flex; align-items: center; gap: 8px;
  }
  .orange-text { color: var(--orange); }
  .green-text { color: var(--green); }
  
  .lens-list { list-style: none; padding: 0; margin: 0; }
  .lens-list li {
    position: relative; padding-left: 24px; margin-bottom: 16px;
    font-size: 15px; line-height: 1.6; color: rgba(255,255,255,0.9);
  }
  .lens-list li::before {
    content: "•"; position: absolute; left: 0; top: 0px; font-size: 18px;
  }
  .lens-left li::before { color: var(--orange); }
  
  /* White Card inside the Dark Card */
  .success-card {
    background: var(--white); color: var(--navy);
    border-radius: 12px; padding: 24px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  }
  .success-list { list-style: none; padding: 0; margin: 0; }
  .success-list li {
    display: flex; gap: 12px; margin-bottom: 16px; font-size: 15px; line-height: 1.5;
  }
  .check-icon {
    min-width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid var(--green);
    display: grid; place-items: center; color: var(--green); font-size: 12px; font-weight: 900;
  }

  /* --- Navigation Controls --- */
  .nav-strip {
    display: flex; gap: 12px; justify-content: center; margin-bottom: 24px;
  }
  .nav-btn {
    background: white; border: 1px solid var(--line);
    padding: 10px 16px; border-radius: 99px;
    font-size: 13px; font-weight: 600; color: var(--muted);
    cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; gap: 8px;
  }
  .nav-btn:hover { border-color: var(--brand); color: var(--brand); }
  .nav-btn.active {
    background: var(--brand); border-color: var(--brand); color: white;
    box-shadow: 0 4px 12px rgba(0, 86, 210, 0.3);
  }
  .nav-circle {
    width: 8px; height: 8px; border-radius: 50%; background: currentColor; opacity: 0.5;
  }
  .nav-btn.active .nav-circle { opacity: 1; background: white; }

  /* --- Source Citation Footer --- */
  .source-footer {
    text-align: center; margin-top: 24px; font-size: 12px; color: var(--muted);
  }
  .source-footer a { color: var(--brand); text-decoration: none; }
`;

/* =========================
   2. Data: Extracted from Financial Services PDF
   ========================= */
const FS_DATA = [
  {
    id: "l-and-d",
    role: "L&D Leader",
    title: "Head of Learning & Development",
    desc: "Responsible for closing the experience gap and proving skill acquisition.",
    nightmare: [
      "“66% of hires aren't fully prepared” — we can't hire our way out of this.",
      "Generic content libraries failing compliance audits.",
      "Inability to prove that training = reduced risk to regulators."
    ],
    success: [
      "Structured 'Skills Tracks' for AI & Data roles.",
      "10-point increase in employee engagement (e.g., Axis Bank).",
      "Stackable micro-credentials from trusted partners (Microsoft, IBM)."
    ],
    coursera_unlock: "Coursera Skills Tracks + Career Graph"
  },
  {
    id: "it-data",
    role: "IT & Data Leader",
    title: "CIO / Chief Data Officer",
    desc: "Managing the $97B AI spend while ensuring security and velocity.",
    nightmare: [
      "Shadow AI usage exposing client data.",
      "Automating 39% of work without a governance framework.",
      "Tech teams stalling because they lack 'hands-on' GenAI experience."
    ],
    success: [
      "2x more AI use cases deployed vs. competitors.",
      "Safe experimentation via 'GenAI Playgrounds' and sandboxes.",
      "Tech stack growth averaging 21.5% at scale."
    ],
    coursera_unlock: "Coursera Labs + GenAI Academy"
  },
  {
    id: "hr-func",
    role: "HR & Functional Exec",
    title: "CHRO / Business Unit Lead",
    desc: "Aligning human capital with the new AI-driven operating model.",
    nightmare: [
      "Entry-level roles shrinking due to automation/offshoring.",
      "Retention plummeting as staff fear obsolescence.",
      "Siloed teams (Compliance vs. Product) blocking innovation."
    ],
    success: [
      "Internal hiring ratio > 50% through 'upskilling in place'.",
      "Broadened recruiting pipelines focusing on adjacent skills.",
      "Unified language for skills across the enterprise."
    ],
    coursera_unlock: "Skills-First Hiring + Internal Mobility"
  },
  {
    id: "c-suite",
    role: "C-Suite / Board",
    title: "CEO / Board of Directors",
    desc: "Shifting AI from a cost-cutter to a revenue growth engine.",
    nightmare: [
      "Treating AI only as an efficiency play, missing the growth curve.",
      "Falling behind: 70% of peers expect AI to lift revenue.",
      "Reputational damage from an AI hallucination or failure."
    ],
    success: [
      "AI driving personalized customer experiences (Revenue Growth).",
      "Verifiable ROI on the $97B industry spend.",
      "Partnering with higher-ed to accelerate readiness."
    ],
    coursera_unlock: "Executive Education + Leadership Academy"
  }
];

/* =========================
   3. Main Component
   ========================= */
export default function PersonaCarousel() {
  const [activeIdx, setActiveIdx] = useState(0);
  const activeData = FS_DATA[activeIdx];

  return (
    <div className="carousel-wrap">
      <style>{CSS}</style>
      
      <div className="carousel-container">
        
        {/* Navigation Strip */}
        <div className="nav-strip">
          {FS_DATA.map((item, idx) => (
            <button 
              key={item.id}
              className={`nav-btn ${idx === activeIdx ? 'active' : ''}`}
              onClick={() => setActiveIdx(idx)}
            >
              <div className="nav-circle" />
              {item.role}
            </button>
          ))}
        </div>

        {/* The "Lens" Card */}
        <div className="lens-card">
          {/* Header */}
          <div className="lens-header">
            <div>
              <span className="lens-tag">Financial Services • AI Era</span>
              <h2 className="lens-title">{activeData.title}</h2>
              <p className="lens-sub">{activeData.desc}</p>
            </div>
          </div>

          {/* Grid Content */}
          <div className="lens-grid">
            
            {/* Left: Nightmares (Orange) */}
            <div className="lens-left">
              <div className="lens-h3 orange-text">
                <span style={{fontSize:16}}>⚠️</span> What Keeps Them Up At Night
              </div>
              <ul className="lens-list">
                {activeData.nightmare.map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>

              <div style={{marginTop: 40}}>
                <div className="lens-h3" style={{color:'rgba(255,255,255,0.5)'}}>
                  <span style={{fontSize:16}}>🔐</span> The Coursera Unlock
                </div>
                <div style={{fontStyle:'italic', color:'white', fontSize:18, borderLeft:'3px solid var(--brand)', paddingLeft:16}}>
                  {activeData.coursera_unlock}
                </div>
              </div>
            </div>

            {/* Right: Success (Green Card) */}
            <div className="lens-right">
              <div className="success-card">
                <div className="lens-h3 green-text" style={{marginBottom:16}}>
                  <span style={{fontSize:16}}>✅</span> What Success Looks Like
                </div>
                <ul className="success-list">
                  {activeData.success.map((point, i) => (
                    <li key={i}>
                      <div className="check-icon">✓</div>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>

        {/* Footer Citation */}
        <div className="source-footer">
          Insights derived from <b>"Closing the Skills Gap: Preparing Financial Services for the AI Era"</b> • <a href="#">Download Report</a>
        </div>

      </div>
    </div>
  );
}
